import { z } from 'zod';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/button';
import { TextInput } from '@/components/inputs/text-input';
import { useNavigate, useSearchParams } from '@/util/react-router-dom';
import { isValidPassword, zt } from '@/util/validators';
import { PasswordHelperList } from '@/components/auth/password-helper-list';
import { usePasswordReset } from '@/api/password-reset';
import { useForgotPassword } from '@/api/forgot-password';
import { useTranslation } from '@/contexts/i18n-context';
import { useVerifyResetToken } from '@/api/verify-reset-token';
import { TokenValidationStatus } from '@/api/api.schemas';
import { JwtService } from '@/services/jwt-service';
import { UserEntryLayout } from '@/components/auth/user-entry-layout';
import { Section } from '@/components/containers/section';
import { UserEntryFormContainer } from '@/components/auth/user-entry-form-container';
import styles from './reset-password-page.module.css';
import { useBranding } from '@/outlets/branding-outlet';
import { BrokenLinkSvg } from '@/components/svgs/broken-link';

const schema = z.object({
  password: z.string().refine(isValidPassword, { message: zt('passwordReset.reset.error') }),
});

type FormValues = z.infer<typeof schema>;

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const { networkId } = useBranding();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
  });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? undefined;
  const { mutate: verifyToken, error: verifyTokenError } = useVerifyResetToken();
  const { mutate: resetPassword, isPending, error } = usePasswordReset();
  const { mutate: forgotPassword, isPending: isForgotPasswordPending } = useForgotPassword();

  useEffect(() => {
    if (token) {
      verifyToken(
        { data: { token } },
        {
          onError: (error) => {
            if (error.type === TokenValidationStatus.Invalid) {
              navigate('/not-found');
            }
          },
        },
      );
    }
  }, [token, verifyToken, navigate]);

  const resendEmail = () => {
    const username = JwtService.decode<{ username: string }>(token)?.username;

    if (!username) {
      navigate('/not-found');
      return;
    }

    forgotPassword(
      { data: { username, networkId } },
      {
        onSuccess: () => navigate(`/forgot-password/success`),
      },
    );
  };

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    if (!token) {
      navigate('/not-found');
      return;
    }

    resetPassword(
      { data: { password: data.password, token } },
      {
        onSuccess: () => navigate('/reset-password/success'),
      },
    );
  };

  const password = form.watch('password');

  return (
    <UserEntryLayout>
      {verifyTokenError?.type === TokenValidationStatus.Expired ||
      error?.type === TokenValidationStatus.Expired ? (
        <Section className={styles.brokenLinkSection}>
          <BrokenLinkSvg className={styles.icon} />
          <div className={styles.textSection}>
            <h1 className="h3">{t('passwordReset.reset.invalidLink.title')}</h1>
            <p>{t('passwordReset.reset.invalidLink.description')}</p>
          </div>
          <div className={styles.buttonContainer}>
            <Button
              className={styles.actionButton}
              fullWidth
              onClick={resendEmail}
              loading={isForgotPasswordPending}
            >
              {t('passwordReset.reset.requestNewEmail')}
            </Button>
            <Button
              className={styles.actionButton}
              as="a"
              to="/login"
              variant="outlined"
              fullWidth
              rightIcon="arrow_forward"
            >
              {t('passwordReset.reset.backToLogin')}
            </Button>
          </div>
          <p className={styles.contactUs}>
            {t('passwordReset.forgot.support.text', {
              link: (
                <Button key="contact-support-button" variant="link" as="a" to="/support">
                  {t('passwordReset.forgot.support.link')}
                </Button>
              ),
            })}
          </p>
        </Section>
      ) : (
        <UserEntryFormContainer
          title={t('passwordReset.pageTitle')}
          form={form}
          onSubmit={onSubmit}
        >
          <div>
            <TextInput
              name="password"
              label={t('passwordReset.reset.label')}
              type="password"
              placeholder={t('passwordReset.reset.placeholder')}
              withIconsIndicators={false}
            />
            <PasswordHelperList password={password} />
          </div>
          <Button fullWidth loading={isPending} type="submit">
            {t('passwordReset.reset.submit')}
          </Button>
        </UserEntryFormContainer>
      )}
    </UserEntryLayout>
  );
}
